import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getReconOverview, uploadReconStatement, setReconSessionType, computeReconBalance, dismissDuplicate, removeDuplicate, getReconSession, autoMatchSession, reconcileLine } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Landmark, CreditCard, AlertTriangle, Copy, Upload, CheckCircle2, XCircle, RefreshCw, ArrowLeftRight, FileText } from 'lucide-react';

function StatusBadge({ status }) {
  const styles = {
    reconciled: { bg: 'rgba(45,106,79,0.1)', color: '#2D7A5D', label: 'Reconciled' },
    matched: { bg: 'rgba(198,156,109,0.1)', color: '#D98A4A', label: 'Matched' },
    pending: { bg: 'rgba(161,161,165,0.1)', color: '#9CA3AF', label: 'Pending' },
    unmatched: { bg: 'rgba(224,122,95,0.1)', color: '#C84B31', label: 'Unmatched' },
  };
  const s = styles[status] || styles.pending;
  return <span className="px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
}

export default function ReconCenterPage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await getReconOverview();
      setOverview(res.data);
    } catch { toast.error('Failed to load reconciliation data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadReconStatement(file);
      toast.success('Statement uploaded');
      fetchOverview();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDismiss = async (txnId) => {
    try { await dismissDuplicate(txnId); toast.success('Dismissed'); fetchOverview(); } catch { toast.error('Failed'); }
  };
  const handleRemove = async (txnId) => {
    try { await removeDuplicate(txnId); toast.success('Removed'); fetchOverview(); } catch { toast.error('Failed'); }
  };

  if (loading) return <Layout title="Reconciliation Center"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D98A4A', borderTopColor: 'transparent' }}></div></div></Layout>;

  const { bank_accounts = [], credit_cards = [], total_reconciled = 0, total_unreconciled = 0, total_variances = 0, duplicate_candidates = [], duplicate_count = 0 } = overview || {};

  return (
    <Layout title="Reconciliation Center">
      <div data-testid="recon-center-page" className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Reconciled', value: total_reconciled, icon: CheckCircle2, color: '#2D7A5D' },
            { label: 'Unreconciled', value: total_unreconciled, icon: XCircle, color: '#C84B31' },
            { label: 'Open Variances', value: total_variances, icon: AlertTriangle, color: '#D98A4A' },
            { label: 'Duplicate Candidates', value: duplicate_count, icon: Copy, color: '#D98A4A' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-xl p-5" style={{ border: '1px solid #E5E3DB' }} data-testid={`recon-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: '#9CA3AF' }}>{label}</p>
                  <p className="text-2xl font-heading font-medium" style={{ color: '#1A1A1A' }}>{value}</p>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
                  <Icon size={20} style={{ color }} strokeWidth={1.8} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Upload */}
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-medium" style={{ color: '#1A1A1A' }}>
            <span className="italic" style={{ color: '#D98A4A' }}>Account</span> Statements
          </h2>
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={handleUpload} className="hidden" data-testid="recon-upload-input" />
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body font-medium text-sm text-white transition-colors" style={{ background: '#C84B31' }}>
              <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Statement'}
            </span>
          </label>
        </div>

        <Tabs defaultValue="bank">
          <TabsList className="p-1 rounded-xl" style={{ background: '#E5E3DB' }}>
            <TabsTrigger value="bank" className="rounded-lg font-body data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-bank-accounts">
              <Landmark size={14} className="mr-2" /> Bank Accounts ({bank_accounts.length})
            </TabsTrigger>
            <TabsTrigger value="credit" className="rounded-lg font-body data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-credit-cards">
              <CreditCard size={14} className="mr-2" /> Credit Cards ({credit_cards.length})
            </TabsTrigger>
            <TabsTrigger value="duplicates" className="rounded-lg font-body data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-duplicates">
              <Copy size={14} className="mr-2" /> Duplicates ({duplicate_count})
            </TabsTrigger>
          </TabsList>

          {/* Bank Accounts Tab */}
          <TabsContent value="bank" className="mt-4">
            {bank_accounts.length > 0 ? (
              <div className="space-y-3">
                {bank_accounts.map((s) => (
                  <AccountCard key={s.session_id} session={s} onRefresh={fetchOverview} />
                ))}
              </div>
            ) : (
              <EmptyState icon={Landmark} message="No bank statements uploaded yet" />
            )}
          </TabsContent>

          {/* Credit Cards Tab */}
          <TabsContent value="credit" className="mt-4">
            {credit_cards.length > 0 ? (
              <div className="space-y-3">
                {credit_cards.map((s) => (
                  <AccountCard key={s.session_id} session={s} onRefresh={fetchOverview} />
                ))}
              </div>
            ) : (
              <EmptyState icon={CreditCard} message="No credit card statements uploaded yet" />
            )}
          </TabsContent>

          {/* Duplicates Tab */}
          <TabsContent value="duplicates" className="mt-4">
            {duplicate_candidates.length > 0 ? (
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
                <div className="px-6 py-4" style={{ borderBottom: '1px solid #E5E3DB' }}>
                  <h3 className="font-heading text-lg font-medium">Duplicate Candidates</h3>
                  <p className="text-xs font-body" style={{ color: '#9CA3AF' }}>Transactions with matching date, vendor, and amount</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full table-dense">
                    <thead><tr className="text-left"><th className="px-6 pt-4">Date</th><th className="px-6 pt-4">Vendor</th><th className="px-6 pt-4 text-right">Amount</th><th className="px-6 pt-4">Actions</th></tr></thead>
                    <tbody>
                      {duplicate_candidates.map((d) => (
                        <tr key={d.transaction_id}>
                          <td className="px-6 font-mono text-sm">{d.date}</td>
                          <td className="px-6 text-sm">{d.vendor_name}</td>
                          <td className="px-6 text-right font-mono text-sm">${Math.abs(d.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td className="px-6">
                            <div className="flex gap-2">
                              <button onClick={() => handleDismiss(d.transaction_id)} className="text-xs font-body px-2 py-1 rounded-lg" style={{ background: 'rgba(198,156,109,0.1)', color: '#D98A4A' }} data-testid={`dismiss-dup-${d.transaction_id}`}>Dismiss</button>
                              <button onClick={() => handleRemove(d.transaction_id)} className="text-xs font-body px-2 py-1 rounded-lg" style={{ background: 'rgba(157,2,8,0.1)', color: '#C84B31' }} data-testid={`remove-dup-${d.transaction_id}`}>Remove</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <EmptyState icon={Copy} message="No duplicate candidates found" />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function AccountCard({ session, onRefresh }) {
  const s = session;
  const matchPct = s.total_lines > 0 ? Math.round((s.matched / s.total_lines) * 100) : 0;
  return (
    <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E5E3DB' }} data-testid={`recon-session-${s.session_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {s.account_type === 'credit_card' ? <CreditCard size={18} style={{ color: '#D98A4A' }} /> : <Landmark size={18} style={{ color: '#D98A4A' }} />}
          <div>
            <h4 className="font-body font-medium text-sm" style={{ color: '#1A1A1A' }}>{s.account_name}</h4>
            <p className="text-xs font-body" style={{ color: '#9CA3AF' }}>{s.filename} &middot; {s.total_lines} lines</p>
          </div>
        </div>
        <StatusBadge status={s.status} />
      </div>
      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#E5E3DB' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${matchPct}%`, background: matchPct === 100 ? '#2D7A5D' : '#D98A4A' }}></div>
        </div>
        <span className="text-xs font-mono font-medium" style={{ color: matchPct === 100 ? '#2D7A5D' : '#D98A4A' }}>{matchPct}%</span>
      </div>
      {/* Stats */}
      <div className="flex items-center gap-6 text-xs font-body" style={{ color: '#4B5563' }}>
        <span><strong style={{ color: '#2D7A5D' }}>{s.matched}</strong> matched</span>
        <span><strong style={{ color: '#C84B31' }}>{s.unmatched}</strong> unmatched</span>
        <span><strong style={{ color: '#1A1A1A' }}>{s.reconciled}</strong> reconciled</span>
        {s.variance !== null && s.variance !== undefined && (
          <span>Variance: <strong className="font-mono" style={{ color: s.variance === 0 ? '#2D7A5D' : '#C84B31' }}>${Math.abs(s.variance).toFixed(2)}</strong></span>
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, message }) {
  return (
    <div className="bg-white rounded-xl p-12 text-center" style={{ border: '1px solid #E5E3DB' }}>
      <Icon size={32} className="mx-auto mb-3" style={{ color: '#9CA3AF' }} />
      <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>{message}</p>
    </div>
  );
}
